import { ManifestLink } from '@remix-pwa/sw';
import { Links, Meta, Scripts, useLocation, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import cx from 'classnames';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import FacebookPixel from '~/components/Pixels/FacebookPixel';
import GA4Pixel from '~/components/Pixels/GA4Pixel';
import { GTMPixel, GTMScript } from '~/components/Pixels/GTMPixel';
import TiktokPixel from '~/components/Pixels/TiktokPixel';
import TwitterPixel from '~/components/Pixels/TwitterPixel';

import type { Publication } from '~/interfaces/publication';
import type { Site } from '~/interfaces/site';

import { useDisplayMode } from '~/hooks/useDisplayMode';
import type { CookieValues } from '~/hooks/useUtils/useCookies';

import { ComponentErrorBoundary } from '../ComponentErrorBoundary';
import GSCVerification from '../Pixels/GSCVerification';

import { AppGlobalsScript } from '~/components/AppGlobals';
import getRootStyles from '~/utils/getRootStyles';
import GlobalHeaderScript from '../Pixels/GlobalHeaderScript';

import type { RootLoaderData } from '~/root';
import EmbedSupportScript from '../EmbedSupportScript';
import { FontStyles } from '../FontStyles';
import { ScrollRestoration } from '../ScrollRestoration';

type Props = PropsWithChildren<{
  isProdEnv?: boolean;
  rootStyles?: any;
  cookies?: CookieValues;
  site?: Site;
  publication?: Publication;
  title?: string;
}>;

const Document = ({ site, children, title, publication, isProdEnv, rootStyles, cookies }: Props) => {
  const { noScript } = (useRouteLoaderData('root') ?? {}) as RootLoaderData;
  const location = useLocation();
  const { i18n } = useTranslation();
  const displayMode = useDisplayMode();

  const [searchParams] = useSearchParams();
  const preventScrollOnFilter = searchParams.has('tags') || (searchParams.has('q') && location.pathname === '/');

  // Compliance
  const cookiesBanner = site?.viewable_site_version?.settings?.cookie_banner_enabled;
  const cookiesLocalStorage = cookies?.beehiiv_honey;

  let marketingCookies = !cookiesBanner;
  let dataProcessorCookies = !cookiesBanner;

  if (cookiesBanner) {
    marketingCookies =
      cookiesBanner && cookiesLocalStorage
        ? JSON.parse(cookiesLocalStorage)?.marketing || false
        : !(cookiesBanner && !cookiesLocalStorage);
    dataProcessorCookies =
      cookiesBanner && cookiesLocalStorage
        ? JSON.parse(cookiesLocalStorage)?.dataProcessing || false
        : !(cookiesBanner && !cookiesLocalStorage);
  }

  // Pixels
  const facebookPixel = site?.viewable_site_version?.settings?.meta_pixel_id;
  const twitterPixel = site?.viewable_site_version?.settings?.x_pixel_id;
  const tiktokPixel = site?.viewable_site_version?.settings?.tiktok_pixel_id;
  // TODO: Check with Mike if this is deprecated?
  // const gaPixel = publication?.google_analytics_pixel;
  const ga4Pixel = site?.viewable_site_version?.settings?.ga4_pixel_id;
  const gtmPixel = site?.viewable_site_version?.settings?.gtm_pixel_id;
  const gscCode = site?.viewable_site_version?.settings?.gsc_verification_code;
  const globalScript = site?.viewable_site_version?.settings?.global_header_script_blob;

  const viewport = `width=device-width, initial-scale=1, shrink-to-fit=no${displayMode === 'standalone' ? ', user-scalable=no' : ''}`;

  const isPostPage = location.pathname.startsWith('/p/');

  let bodyClass = '';

  if (isPostPage) {
    bodyClass = 'bg-wt-background text-wt-text-on-background';
  }

  return (
    <html lang={i18n?.language || 'en'} className="h-full antialiased">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content={viewport} />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="theme-color" content="#000000" />
        <AppGlobalsScript />
        {title ? <title>{title}</title> : null}
        {/* set default colors in case root loader fails and cant return root styles for error boundary*/}
        {/* these colors will be overridden below from getRootStyles() from root loader*/}
        <style
          dangerouslySetInnerHTML={{
            __html: `
:root {
  --wt-primary-color: #030712;
  --wt-text-on-primary-color: #F9FAFB;

  --wt-background-color: #FFFFFF;
  --wt-text-on-background-color: #030712;
}
`,
          }}
        />
        {publication?.web_template?.pwa_enabled && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html: `
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__DeferredPrompt__ = e;
});
          `,
              }}
            />
            <ManifestLink />
          </>
        )}
        <Meta />
        <FontStyles />
        <style
          suppressHydrationWarning={true}
          dangerouslySetInnerHTML={{
            __html:
              isPostPage && publication?.web_theme
                ? getRootStyles(
                    publication,
                    site?.viewable_site_version?.settings?.post_page_background_color,
                    site?.viewable_site_version?.settings?.post_page_text_color,
                  )
                : rootStyles,
          }}
        />
        <link rel="apple-touch-icon" href={publication?.logo?.thumb?.url} />
        <link rel="apple-touch-startup-image" href={publication?.thumbnail?.url} />
        <Links />
        {isProdEnv && (
          <ComponentErrorBoundary
            fallbackRender={() => {
              return null;
            }}
          >
            {gscCode && <GSCVerification code={gscCode} />}
            {gtmPixel && <GTMScript dataProcessingCookies={dataProcessorCookies} pixelId={gtmPixel} />}
            {facebookPixel && <FacebookPixel marketingCookies={marketingCookies} pixelId={facebookPixel} />}
            {twitterPixel && <TwitterPixel marketingCookies={marketingCookies} pixelId={twitterPixel} />}
            {tiktokPixel && <TiktokPixel marketingCookies={marketingCookies} pixelId={tiktokPixel} />}
            {ga4Pixel && <GA4Pixel marketingCookies={marketingCookies} pixelId={ga4Pixel} />}
          </ComponentErrorBoundary>
        )}
        {globalScript && <GlobalHeaderScript script={globalScript} />}
        <EmbedSupportScript />
      </head>
      <body suppressHydrationWarning className={cx(bodyClass)}>
        {isProdEnv && gtmPixel && (
          <ComponentErrorBoundary
            fallbackRender={() => {
              return null;
            }}
          >
            <GTMPixel dataProcessingCookies={dataProcessorCookies} pixelId={gtmPixel} />
          </ComponentErrorBoundary>
        )}
        {/** Prevent any user clicks when previewing so the user doesn't navigate to the wrong page in the iframe */}
        {children}
        {!preventScrollOnFilter && <ScrollRestoration />}
        {!noScript && <Scripts />}
      </body>
    </html>
  );
};

export default Document;
